import { useEffect, useMemo } from 'react';
import { dayjs, formatDate } from '@koob/dates';
import { getBookedRoomsComposition } from '@koob/utils';
import useTripStore from '../../store/useTripStore';

// Split out of useTripDayHotelData (US-061) to stay under the package's
// max-lines-per-function cap. Two derived-value hooks and three effects,
// each triggered by a different change the hotel-stay group cares about;
// none has a use for the others' inputs.

// The stay's [start, end) date range, from the group's start day and the
// trip's departure date — undefined until both are known.
export function useHotelStayDateRange({
  hotelGroup,
  departureDate,
}: {
  hotelGroup: any;
  departureDate: any;
}): { roomStartDate?: string; roomEndDate?: string } {
  // A group always has a numeric start: undefined means no group. The
  // group object itself is rebuilt on every store change.
  const startDayIndex = hotelGroup?.startDayIndex;
  const nights = hotelGroup?.nights;
  return useMemo(() => {
    if (startDayIndex === undefined || !departureDate) return {};

    const startDate = formatDate(
      dayjs(departureDate).add(startDayIndex, 'days'),
      'YYYY-MM-DD',
    );
    const endDate = formatDate(
      dayjs(departureDate).add(startDayIndex + nights, 'days'),
      'YYYY-MM-DD',
    );

    return { roomStartDate: startDate, roomEndDate: endDate };
  }, [startDayIndex, nights, departureDate]);
}

// Whether the stay's rooms-availability request is still pending, or came
// back with an error — read off the group's first day, since only that day
// ever runs the request (useTripDayHotelData's useHotelRoomAvailability call
// is `enabled` there only).
export function useHotelGroupStatus({
  hotelGroup,
  isIncludedHotel,
  isFirstInGroup,
  days,
  roomsError,
  roomsData,
}: {
  hotelGroup: any;
  isIncludedHotel: any;
  isFirstInGroup: boolean;
  days: any[];
  roomsError: any;
  roomsData: any;
}): { isGroupLoading: boolean; hasGroupError: boolean } {
  const startDayIndex = hotelGroup?.startDayIndex;
  const isGroupLoading = useMemo(() => {
    if (startDayIndex === undefined || isIncludedHotel) return false;
    if (isFirstInGroup && (roomsError || roomsData !== undefined)) {
      return false;
    }
    const firstDay = days[startDayIndex];
    return firstDay?.hotelIsLoading === true;
  }, [
    startDayIndex,
    days,
    isIncludedHotel,
    isFirstInGroup,
    roomsError,
    roomsData,
  ]);

  const hasGroupError = useMemo(() => {
    if (startDayIndex === undefined || isIncludedHotel) return false;
    if (isFirstInGroup) return !!roomsError;
    const firstDay = days[startDayIndex];
    return firstDay?.hotelHasError === true;
  }, [startDayIndex, days, isIncludedHotel, isFirstInGroup, roomsError]);

  return { isGroupLoading, hasGroupError };
}

// Store loading/error state on the first day's data so every day in the
// group can read it (navbar + this day's own row).
export function useSyncHotelGroupLoadingState({
  dayIndex,
  hotel,
  isFirstInGroup,
  hotelGroup,
  days,
  day,
  roomsData,
  roomsLoading,
  roomsFetching,
  roomsError,
  updateDay,
}: {
  dayIndex: number;
  hotel: any;
  isFirstInGroup: boolean;
  hotelGroup: any;
  days: any[];
  day: any;
  roomsData: any;
  roomsLoading: boolean;
  roomsFetching: boolean;
  roomsError: any;
  updateDay: (dayIndex: number, updates: Record<string, any>) => void;
}) {
  useEffect(() => {
    if (isFirstInGroup && hotel?.id) {
      const hasData = roomsData !== undefined;
      const isLoading =
        roomsError || hasData ? false : roomsLoading || roomsFetching;
      const currentLoadingState = day?.hotelIsLoading;
      const currentErrorState = day?.hotelHasError;
      const hasError = !!roomsError;

      if (currentLoadingState !== isLoading || currentErrorState !== hasError) {
        if (hotelGroup) {
          const updates: Record<number, any> = {};
          hotelGroup.dayIndices.forEach((idx: number) => {
            updates[idx] = {
              hotelIsLoading: isLoading,
              hotelHasError: hasError,
            };
          });

          const newDays = [...days];
          hotelGroup.dayIndices.forEach((idx: number) => {
            newDays[idx] = { ...newDays[idx], ...updates[idx] };
          });
          useTripStore.getState().updateDays(newDays);
        } else {
          updateDay(dayIndex, {
            hotelIsLoading: isLoading,
            hotelHasError: hasError,
          });
        }
      }
    }
  }, [
    isFirstInGroup,
    hotel?.id,
    roomsLoading,
    roomsFetching,
    roomsError,
    roomsData,
    dayIndex,
    updateDay,
    day?.hotelIsLoading,
    day?.hotelHasError,
    hotelGroup,
    days,
  ]);
}

// Clear selected room when nights change (forces re-selection with new pricing)
export function useClearRoomSelectionOnNightsChange({
  dayIndex,
  hotel,
  isFirstInGroup,
  nights,
  days,
  dayRoomSelection,
  updateDay,
}: {
  dayIndex: number;
  hotel: any;
  isFirstInGroup: boolean;
  nights: number;
  days: any[];
  dayRoomSelection: any;
  updateDay: (dayIndex: number, updates: Record<string, any>) => void;
}) {
  useEffect(() => {
    if (hotel?.id && isFirstInGroup && nights) {
      const currentDay = days[dayIndex];
      const storedNights = currentDay?.hotelNights;

      // A day that has just BECOME the first of its group — the middle day of
      // a longer stay was removed, splitting it in two — never recorded its
      // nights, so the comparison below cannot see the change. It may still
      // carry pricing and a room computed for the OLD grouping: treat that as
      // a change too, or a 3-night rate gets relabelled "1 night" and the
      // stay looks bookable.
      const hasStaleGroupPricing =
        storedNights === undefined &&
        Boolean(currentDay?.hotelPricing || dayRoomSelection);

      if (
        (storedNights !== undefined && storedNights !== nights) ||
        hasStaleGroupPricing
      ) {
        const previousRoomName = dayRoomSelection?.roomData?.name;
        const previousRoomKey = dayRoomSelection?.roomKey ?? null;

        updateDay(dayIndex, {
          hotelPricing: null,
          hotelNights: nights,
          selectedRoom: null,
          previouslySelectedRoomName: previousRoomName,
          hotelSelectedRoomKey: previousRoomKey,
        });
      } else if (storedNights === undefined) {
        updateDay(dayIndex, { hotelNights: nights });
      }
    }
  }, [
    nights,
    hotel?.id,
    isFirstInGroup,
    dayIndex,
    updateDay,
    days,
    dayRoomSelection,
  ]);
}

// Clear selected room when room count changes (forces re-selection with new composition)
export function useClearRoomSelectionOnRoomCountChange({
  dayIndex,
  hotel,
  isFirstInGroup,
  roomsComposition,
  days,
  dayRoomSelection,
  updateDay,
}: {
  dayIndex: number;
  hotel: any;
  isFirstInGroup: boolean;
  roomsComposition: any[];
  days: any[];
  dayRoomSelection: any;
  updateDay: (dayIndex: number, updates: Record<string, any>) => void;
}) {
  const currentRoomCount = roomsComposition?.length;
  useEffect(() => {
    if (hotel?.id && isFirstInGroup && currentRoomCount !== undefined) {
      const currentDay = days[dayIndex];
      const storedRoomCount = currentDay?.hotelRoomCount;

      if (
        storedRoomCount !== undefined &&
        storedRoomCount !== currentRoomCount
      ) {
        // Don't preserve room selection when count changes - always select cheapest
        updateDay(dayIndex, {
          hotelPricing: null,
          hotelRoomCount: currentRoomCount,
          selectedRoom: null,
          previouslySelectedRoomNames: null,
          hotelSelectedRoomKey: null,
        });
      } else if (storedRoomCount === undefined) {
        updateDay(dayIndex, { hotelRoomCount: currentRoomCount });
      }
    }
  }, [
    currentRoomCount,
    hotel?.id,
    isFirstInGroup,
    dayIndex,
    updateDay,
    days,
    dayRoomSelection,
  ]);
}

// Whether the stay's rooms-availability request should run at all: not for
// an included hotel, not for a group whose first day hasn't loaded yet, not
// for a hotel we cannot even view, and — DMC only — not for a hotel already
// reopened from a confirmed booking, whose price/rooms come from
// `hotel.previousBooking` instead (a fresh fetch would auto-select a
// "cheapest available" room over it and silently change what gets saved).
export function isHotelAvailabilityQueryEnabled({
  isFirstInGroup,
  isIncludedHotel,
  hotelIsNotDistributed,
  hotel,
}: {
  isFirstInGroup: boolean;
  isIncludedHotel: any;
  hotelIsNotDistributed: boolean;
  hotel: any;
}): boolean {
  return (
    isFirstInGroup &&
    !isIncludedHotel &&
    !hotelIsNotDistributed &&
    !hotel?.previousBooking
  );
}

// A hotel reopened from an already-confirmed booking (DMC only — TO's
// hotels never carry `previousBooking`) shows the rooms it was actually
// booked with, not the trip's generic composition, whenever the day itself
// never got its own explicit split (`stayRooms`).
export function useDisplayedRooms({
  hotel,
  stayRooms,
  roomsComposition,
}: {
  hotel: any;
  stayRooms: any;
  roomsComposition: any[];
}): { bookedRooms: any[]; displayedRooms: any[] } {
  const bookedRooms = useMemo(
    () => getBookedRoomsComposition(hotel?.previousBooking?.roomsBooked),
    [hotel?.previousBooking],
  );
  const displayedRooms =
    !stayRooms && hotel?.previousBooking ? bookedRooms : roomsComposition;

  return { bookedRooms, displayedRooms };
}
